import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Content,
  EmptyState,
  Header,
  Link,
  Page,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import SearchIcon from '@material-ui/icons/Search';
import { useMarketplace, useSourceUrl } from './api';
import type { MarketplacePlugin } from './api';

/**
 * Browse of the Claude plugin marketplace.
 *
 * Same shape as the catalog browse on purpose — a filter bar
 * over a card grid — so this reads as another room in the portal rather than a
 * bolted-on viewer.
 */

const useStyles = makeStyles(theme => ({
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'center',
    marginBottom: 20,
  },
  search: { flex: '1 1 260px', minWidth: 220 },
  select: { width: 190 },
  barEnd: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginLeft: 'auto',
  },
  count: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
  },
  blurb: {
    color: 'var(--ar-fg-2)',
    fontSize: '0.875rem',
    marginBottom: 18,
    maxWidth: 720,
  },
  setup: {
    maxWidth: 720,
    margin: '0 0 14px',
    padding: '10px 14px',
    background: 'var(--ar-surface-2)',
    border: '1px solid var(--ar-border-soft)',
    borderRadius: 6,
    fontSize: '0.8125rem',
    lineHeight: 1.7,
    overflowX: 'auto',
    color: 'var(--ar-fg)',
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    textDecoration: 'none',
    transition: 'border-color 120ms ease',
    '&:hover': { borderColor: 'var(--ar-accent)' },
  },
  cardRule: { height: 3, flexShrink: 0, background: 'var(--ar-gradient)' },
  cardHeader: { padding: '14px 16px 0' },
  cardCategory: {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
    marginBottom: 3,
  },
  cardTitle: {
    fontSize: '1.0625rem',
    fontWeight: 640,
    letterSpacing: '-0.011em',
    color: theme.palette.primary.main,
  },
  cardBody: { flex: 1, display: 'flex', flexDirection: 'column', gap: 10 },
  desc: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    color: 'var(--ar-fg-2)',
    fontSize: '0.8125rem',
  },
  meta: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  // What the plugin actually contains — the reason to open it.
  counts: {
    display: 'flex',
    gap: 16,
    padding: '10px 16px',
    borderTop: '1px solid var(--ar-border-soft)',
    fontSize: '0.8125rem',
    color: 'var(--ar-fg-2)',
  },
  countNum: { color: 'var(--ar-fg)', fontWeight: 600 },
  version: {
    marginLeft: 'auto',
    color: 'var(--ar-fg-3)',
    fontVariantNumeric: 'tabular-nums',
  },
}));

const ANY = '__any__';

/**
 * What the plugin contains, as a footer line. Zero counts are omitted rather
 * than rendered as "0 agents" — the line is there to say what you get, and a
 * row of zeroes reads as absence of data rather than absence of the thing.
 */
const countsFor = (p: MarketplacePlugin) => {
  const { skills, agents, commands, hooksPath } = p.contents;
  const counted = [
    { n: skills.length, one: 'skill', many: 'skills' },
    { n: agents.length, one: 'agent', many: 'agents' },
    { n: commands.length, one: 'command', many: 'commands' },
  ]
    .filter(c => c.n > 0)
    .map(c => ({ label: c.n === 1 ? c.one : c.many, n: c.n, bare: false }));

  // Hooks are one file describing many events, so a count would be misleading.
  return hooksPath
    ? [...counted, { label: 'hooks', n: 0, bare: true }]
    : counted;
};

/**
 * Placeholder cards in the real cards' grid and proportions, so the layout does
 * not jump when the data lands. Eight fills two rows at the widest breakpoint;
 * the marketplace holds about that many plugins.
 */
const SkeletonGrid = () => {
  const classes = useStyles();
  return (
    <Grid container spacing={2} data-testid="marketplace-skeleton">
      {Array.from({ length: 8 }, (_, i) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
          <Card className={classes.card}>
            <div className={classes.cardRule} />
            <div className={classes.cardHeader}>
              <Skeleton variant="text" width="30%" />
              <Skeleton variant="text" width="60%" height={28} />
            </div>
            <CardContent className={classes.cardBody}>
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" width="70%" />
            </CardContent>
            <div className={classes.counts}>
              <Skeleton variant="text" width="50%" />
            </div>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export const MarketplacePage = () => {
  const classes = useStyles();
  const { data, error, loading } = useMarketplace();
  const repoUrl = useSourceUrl();
  const setupUrl = useSourceUrl('README.md');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(ANY);

  const categories = useMemo(
    () =>
      [
        ...new Set(
          (data?.plugins ?? []).map(p => p.category ?? '').filter(Boolean),
        ),
      ].sort(),
    [data],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.plugins ?? [])
      .filter(p => (category === ANY ? true : p.category === category))
      .filter(p =>
        q
          ? `${p.name} ${p.description ?? ''} ${(p.keywords ?? []).join(' ')}`
              .toLowerCase()
              .includes(q)
          : true,
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, category, query]);

  return (
    <Page themeId="home">
      <Header
        title="Marketplace"
        subtitle={data?.manifest.metadata?.description ?? 'Claude Code plugins'}
      />
      <Content>
        {/* Everything that does not depend on the data renders at once. The
            page used to be a bare progress bar until both GitHub reads had
            returned — on a cold backend, seconds of nothing — so the search
            box is usable (and typed-into state kept) while the cards load. */}
        <Typography className={classes.blurb}>
          Skills, agents and hooks published to the{' '}
          {data ? (
            <strong>{data.manifest.name}</strong>
          ) : (
            <Skeleton
              variant="text"
              width={90}
              style={{ display: 'inline-block' }}
            />
          )}{' '}
          marketplace. Browse here to see what each one does, then set it up
          once per machine:
        </Typography>

        {/*
          The GitHub sign-in is part of the command, not a footnote, because
          leaving it out produces a message that sends people looking in the
          wrong place: Claude Code fetches the marketplace by CLONING it, and a
          clone it cannot authenticate is indistinguishable from a repository
          with no manifest. The UI then says the repo "isn't a marketplace …
          make sure you're adding the marketplace repository", so a pure access
          failure reads as a typo in the repo name.

          Measured 2026-09-01 against the org's SAML credential authorizations:
          of 71 members, 25 had an SSO-authorized SSH key — the other 46 could
          not clone the shorthand, which resolves to SSH. PREFER_HTTPS is
          spelled out because the sign-in we document (`gh auth login`)
          configures an HTTPS credential helper the default transport ignores.

          One string rather than JSX text: JSX collapses the newline between two
          lines of children and would indent the second command inside a <pre>,
          which people then have to clean up after copy-paste.
        */}
        <Typography component="pre" className={classes.setup}>
          {'gh auth login && gh auth setup-git\n' +
            'claude plugin marketplace add answerrocket/claude-marketplace'}
        </Typography>

        <Typography className={classes.blurb}>
          The marketplace is a private repository, so the sign-in is a
          prerequisite — without a credential for it, <code>/plugin</code>{' '}
          reports that the repository <em>{`isn't a marketplace`}</em>, which
          means unreadable, not wrong. The <code>owner/repo</code> shorthand
          clones over SSH; if your GitHub access is HTTPS through{' '}
          <code>gh</code>, set <code>CLAUDE_CODE_PLUGIN_PREFER_HTTPS=1</code> so
          it uses the credential you have.{' '}
          {setupUrl && <Link to={setupUrl}>Full setup</Link>}
        </Typography>

        <div className={classes.filterBar}>
          <TextField
            className={classes.search}
            variant="outlined"
            size="small"
            placeholder="Search plugins"
            value={query}
            onChange={e => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {categories.length > 1 && (
            <TextField
              select
              variant="outlined"
              size="small"
              className={classes.select}
              label="Category"
              value={category}
              onChange={e => setCategory(e.target.value)}
            >
              <MenuItem value={ANY}>All categories</MenuItem>
              {categories.map(c => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          )}
          <div className={classes.barEnd}>
            <span className={classes.count}>
              {data ? (
                `${visible.length} ${
                  visible.length === 1 ? 'plugin' : 'plugins'
                }`
              ) : (
                <Skeleton variant="text" width={60} />
              )}
            </span>
          </div>
        </div>

        {error && <ResponseErrorPanel error={error} />}

        {loading && <SkeletonGrid />}

        {data && visible.length === 0 && (
          <EmptyState
            missing="content"
            title="No plugins match"
            description="Try a different search or clear the filters."
          />
        )}

        {data && visible.length > 0 && (
          <Grid container spacing={2}>
            {visible.map(p => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={p.name}>
                <Card
                  className={classes.card}
                  component={RouterLink as never}
                  {...{ to: `/marketplace/${p.name}` }}
                >
                  <div className={classes.cardRule} />
                  <div className={classes.cardHeader}>
                    <span className={classes.cardCategory}>
                      {p.category || 'plugin'}
                    </span>
                    <Typography className={classes.cardTitle}>
                      {p.name}
                    </Typography>
                  </div>

                  <CardContent className={classes.cardBody}>
                    {p.description && (
                      <Typography className={classes.desc}>
                        {p.description}
                      </Typography>
                    )}
                    {(p.keywords ?? []).length > 0 && (
                      <Box className={classes.meta}>
                        {(p.keywords ?? []).slice(0, 4).map(k => (
                          <Chip key={k} size="small" label={k} />
                        ))}
                      </Box>
                    )}
                  </CardContent>

                  <div className={classes.counts}>
                    {countsFor(p).map(c => (
                      <span key={c.label}>
                        {!c.bare && (
                          <span className={classes.countNum}>{c.n} </span>
                        )}
                        {c.label}
                      </span>
                    ))}
                    {p.version && (
                      <span className={classes.version}>v{p.version}</span>
                    )}
                  </div>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {repoUrl && (
          <Typography className={classes.blurb} style={{ marginTop: 20 }}>
            <Link to={repoUrl}>Source repository</Link>
          </Typography>
        )}
      </Content>
    </Page>
  );
};
